import React, { useState } from 'react';
import { Paper, Typography, useTheme, Box, Button, Tabs, Tab } from '@mui/material';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { tokens } from '../../../theme';
import { motion } from 'framer-motion';

const GuideTabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

const TagSetupGuide = ({ onClose, organization }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const jsContainerCode = organization.hem_contribution_tag
        ? `var cs = document.createElement('script');
cs.src = "https://a.usbrowserspeed.com/cs?pid=${organization.pid}";
cs.type = 'text/javascript';
document.body.appendChild(cs);

var av = document.createElement('script');
av.src = 'https://a.usbrowserspeed.com/metrics.js?pid=${organization.pid}';
av.type = 'text/javascript';
document.body.appendChild(av);`
        : `var cookie_sync = document.createElement('script');
cookie_sync.src = "https://a.usbrowserspeed.com/cs?pid=${organization.pid}";
cookie_sync.type = 'text/javascript';
document.body.appendChild(cookie_sync);`;

    const tagManagerCodeBase = `<script type="text/javascript" src="https://a.usbrowserspeed.com/cs?pid=${organization.pid}"></script>`;
    const tagManagerCodeMetrics = `<script type="text/javascript" src="https://a.usbrowserspeed.com/metrics.js?pid=${organization.pid}"></script>`;
    const tagManagerCode = organization.hem_contribution_tag
        ? `${tagManagerCodeBase}\n${tagManagerCodeMetrics}`
        : tagManagerCodeBase;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
        >
            <Box sx={{ margin: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h2" color={colors.primary[100]}>
                        5x5 Tag Setup Guide
                    </Typography>
                    <Button onClick={onClose} sx={{ color: colors.primary[100] }}>
                        Close
                    </Button>
                </Box>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="tag setup tabs"
                    sx={{
                        marginBottom: 2,
                        '.MuiTab-root': {
                            color: colors.greenAccent[500], // Not selected
                            '&.Mui-selected': {
                                color: colors.greenAccent[300], // Selected
                            },
                            '&:hover': {
                                color: colors.greenAccent[100], // Hover
                            }
                        }
                    }}
                >
                    <Tab label="HTML" />
                    <Tab label="JavaScript" />
                    <Tab label="Containers/JS Fetch" />
                </Tabs>
                <GuideTabPanel value={tabValue} index={0}>
                    <Typography variant="body1" color={colors.grey[100]}>
                        Place the tag onto the HTML of the page manually with something like GTM a tag manager:
                    </Typography>
                    <SyntaxHighlighter language="html" style={a11yDark}>
                        {tagManagerCode}
                    </SyntaxHighlighter>
                </GuideTabPanel>
                <GuideTabPanel value={tabValue} index={1}>
                    <Typography variant="body1" color={colors.grey[100]}>
                        Add the tag programmatically to a page using JavaScript:
                    </Typography>
                    <SyntaxHighlighter language="javascript" style={a11yDark}>
                        {jsContainerCode}
                    </SyntaxHighlighter>
                </GuideTabPanel>
                <GuideTabPanel value={tabValue} index={2}>
                    <Typography variant="body1" color={colors.grey[100]}>
                        This example shows how to fire a request from a container or JS environment using the Fetch API:
                    </Typography>
                    <SyntaxHighlighter language="javascript" style={a11yDark}>
                        {`function syncCookie(visitorId) {
  const protocol = "https"; // Or derive dynamically
  const domain = "a.usbrowserspeed.com";
  const fetchUrl = \`\${protocol}://\${domain}/cs?pid=\${encodeURIComponent("${organization.pid}")}&puid=\${visitorId}\`;

  fetch(fetchUrl, {
    method: 'GET',
    credentials: 'include', // Important: include credentials
    headers: {
      'Accept': 'application/json' // Signal that JSON is expected
    }
  }).then(res => res.json())
    .then(data => console.log("CS Response:", data));
}`}</SyntaxHighlighter>

                    <Typography variant="h4" color={colors.primary[100]} sx={{ mt: 2 }}>
                        Sample JSON Response:
                    </Typography>
                    <SyntaxHighlighter language="json" style={a11yDark}>
                        {`{
  "tuid": "abc123-trovo-id",
  "sha256_lower_case": "abc123sha...",
  "hem": "abc123sha...",
  "md5_lower_case": "abc123md5...",
  "upID": "abc123-upid"
}`}
                    </SyntaxHighlighter>

                    <Typography variant="body1" color={colors.grey[100]}>
                        <br />
                        <strong>Response Field Descriptions:</strong>
                        <ul>
                            <li><code>tuid</code>: Trovo’s internal user ID generated on the first sync.</li>
                            <li><code>sha256_lower_case</code> / <code>hem</code>: The SHA256 hash of the user email, if available.</li>
                            <li><code>md5_lower_case</code>: The MD5 hash of the user email, if provided and matched.</li>
                            <li><code>upID</code>: Unique Partner ID resolved from the request if real-time resolution is active.</li>
                        </ul>
                        Only the fields available and applicable to the request will be returned in the JSON response.
                    </Typography>
                </GuideTabPanel>


                <Typography variant="h3" color={colors.primary[100]}>URL Parameters:</Typography>
                <Typography variant="body1" color={colors.grey[100]}>
                    <br />
                    <Typography variant="h4" color={colors.primary[100]}>Required Fields:</Typography>

                    <code>pid={'{organization.pid}'}</code> : A string data type. This is a constant, generated by Trovo/5x5.
                    <br />
                    <br />
                    <Typography variant="h4" color={colors.primary[100]}>Optional Fields:</Typography>

                    <code>puid={'{partnerUserId}'}</code>: The Customer’s Cookie_id, this may be generated however the customer would like.
                    <br />
                    <code>hem={'{hemSHA256}'}</code>: This is the hashed email using SHA256 expressed as a SQL function: LOWER(SHA256(TRIM(LOWER(plain_text_email)))). If the customer has a hem already for the user, they can use this to provide to our database.
                    <br />
                    <code>r={'{redirectURL}'}</code>: must be properly URL encoded. If 'r' is present, then redirect the cookie to 'r'.
                    <br />
                    <Typography variant="h6" color={colors.primary[100]}>The URL can have the following placeholders in it ${'{HEM_SHA256_LOWERCASE}'} and ${'{UP_ID}'}. These can be resolved in real time as part of the redirect.</Typography>
                </Typography>
            </Box>
        </motion.div>
    );
};

export default TagSetupGuide;
